/**
 * PATH: packages/ui/src/components/email/attachmentClassify.ts
 * PURPOSE:
 *   - Pure file-type classifier: maps an `EmailAttachment` to a
 *     stable `kind` ("pdf" | "image" | "excel" | …) and a
 *     palette key. NO React, NO lucide-react, NO host coupling.
 *
 * ROLE IN ARCHITECTURE:
 *   - Lift-and-shift of ass-dashboard's `classify()` helper out of
 *     `AttachmentChip.tsx` so the same classification feeds the
 *     upcoming universal `<AttachmentChip>` primitive (PR 2b)
 *     AND any non-React surface that needs to label an
 *     attachment (CLI tools, server-rendered emails, etc.).
 *
 * WHY PALETTE KEYS, NOT TAILWIND CLASSES
 *   - The palette is returned as a key (e.g. "rose"). The host
 *     app — which owns its Tailwind config — turns the key into
 *     classes. This keeps `@quotech/ui` free of palette
 *     decisions for hosts that brand differently (Quotech vs
 *     ASS vs 0x3 admin).
 */

import type { EmailAttachment } from './types'

export type AttachmentKind =
  | 'image'
  | 'pdf'
  | 'word'
  | 'excel'
  | 'powerpoint'
  | 'archive'
  | 'audio'
  | 'video'
  | 'code'
  | 'text'
  | 'email'
  | 'other'

export type AttachmentPalette =
  | 'sky'
  | 'rose'
  | 'blue'
  | 'emerald'
  | 'amber'
  | 'violet'
  | 'slate'

export interface AttachmentMeta {
  kind: AttachmentKind
  /** Short label suitable for the chip subtitle ("PDF", "Excel",
   *  "Image", or — for unknown extensions — the upper-cased
   *  extension itself). */
  label: string
  /** Palette key the host app maps to its own Tailwind classes. */
  palette: AttachmentPalette
}

const IMAGE_EXTS = new Set([
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'heic', 'avif',
])
const WORD_EXTS = new Set(['doc', 'docx', 'rtf', 'odt'])
const EXCEL_EXTS = new Set(['xls', 'xlsx', 'xlsm', 'xlsb', 'csv', 'tsv', 'ods'])
const POWERPOINT_EXTS = new Set(['ppt', 'pptx', 'odp'])
const ARCHIVE_EXTS = new Set(['zip', 'rar', '7z', 'tar', 'gz', 'tgz', 'bz2'])
const AUDIO_EXTS = new Set(['mp3', 'wav', 'm4a', 'aac', 'ogg', 'flac'])
const VIDEO_EXTS = new Set(['mp4', 'mov', 'avi', 'wmv', 'mkv', 'webm'])
const CODE_EXTS = new Set([
  'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'rb', 'go', 'rs',
  'c', 'cpp', 'h', 'json', 'yaml', 'yml', 'xml', 'html', 'css', 'sql',
])
const EMAIL_EXTS = new Set(['msg', 'eml'])
const TEXT_EXTS = new Set(['txt', 'log', 'md', 'csv', 'tsv'])

export function classifyAttachment(att: EmailAttachment): AttachmentMeta {
  const mime = (att.content_type || att.mime_type || '').toLowerCase()
  const ext = (
    att.extension || att.filename.split('.').pop() || ''
  ).toLowerCase()

  const matchExt = (set: Set<string>) => set.has(ext)

  if (mime.startsWith('image/') || att.is_image || matchExt(IMAGE_EXTS)) {
    return { kind: 'image', label: 'Image', palette: 'sky' }
  }
  if (mime === 'application/pdf' || att.is_pdf || ext === 'pdf') {
    return { kind: 'pdf', label: 'PDF', palette: 'rose' }
  }
  if (
    mime.includes('wordprocessingml') ||
    mime === 'application/msword' ||
    matchExt(WORD_EXTS)
  ) {
    return { kind: 'word', label: 'Word', palette: 'blue' }
  }
  if (
    mime.includes('spreadsheet') ||
    mime.includes('excel') ||
    matchExt(EXCEL_EXTS)
  ) {
    return { kind: 'excel', label: 'Excel', palette: 'emerald' }
  }
  if (
    mime.includes('presentation') ||
    mime.includes('powerpoint') ||
    matchExt(POWERPOINT_EXTS)
  ) {
    return { kind: 'powerpoint', label: 'PowerPoint', palette: 'amber' }
  }
  if (
    matchExt(ARCHIVE_EXTS) ||
    mime.includes('zip') ||
    mime.includes('compressed')
  ) {
    return { kind: 'archive', label: 'Archive', palette: 'amber' }
  }
  if (mime.startsWith('audio/') || matchExt(AUDIO_EXTS)) {
    return { kind: 'audio', label: 'Audio', palette: 'violet' }
  }
  if (mime.startsWith('video/') || matchExt(VIDEO_EXTS)) {
    return { kind: 'video', label: 'Video', palette: 'violet' }
  }
  if (matchExt(CODE_EXTS)) {
    return { kind: 'code', label: 'Code', palette: 'slate' }
  }
  if (
    mime === 'message/rfc822' ||
    mime === 'application/vnd.ms-outlook' ||
    matchExt(EMAIL_EXTS)
  ) {
    return { kind: 'email', label: 'Email', palette: 'slate' }
  }
  if (mime.startsWith('text/') || matchExt(TEXT_EXTS)) {
    return {
      kind: 'text',
      label: ext.toUpperCase() || 'Text',
      palette: 'slate',
    }
  }

  // Unknown — surface the extension itself so users see *something*
  // useful instead of a generic "FILE" label.
  return {
    kind: 'other',
    label: ext.toUpperCase() || 'File',
    palette: 'slate',
  }
}

/** Format a byte count as a human-readable string ("3.4 MB", "812 KB").
 *  Lifted from ass-dashboard's `lib/utils.ts` so universal components
 *  don't pull a different formatter than the host did. */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0
  let value = bytes
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024
    i += 1
  }
  return `${value < 10 && i > 0 ? value.toFixed(1) : Math.round(value)} ${units[i]}`
}
